import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wand2, Loader2, Check, ArrowRight, UploadCloud, FileText, Sparkles, Copy, RefreshCw, AlertCircle } from "lucide-react";
import PageHead from "@/components/PageHead";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function JDCustomizationStudio() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [resumeText, setResumeText] = useState("");
  const [cvFileName, setCvFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const [jd, setJd] = useState("");
  const [stage, setStage] = useState("input"); // "input" | "analyzing" | "questions" | "rewriting" | "done"
  const [analysis, setAnalysis] = useState(null);
  const [answers, setAnswers] = useState({});
  const [rewriteResult, setRewriteResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Sync with Career Profile if available
  const loadProfile = () => {
    if (user?.profile) {
      const p = user.profile;
      const skillsStr = Array.isArray(p.skills) ? p.skills.join(", ") : p.skills || "Product Strategy, Analytics";
      const formatted = `${user.name || "Candidate"} — ${p.current_role || p.target_role || "Product Specialist"}
Experience: ${p.years_experience || 3}+ years in ${p.current_role || "Product Management"}.
Preferred Work Model: ${p.work_model || "Remote"} (${Array.isArray(p.locations) ? p.locations.join(", ") : "Global"}).
Target Salary: ${p.target_salary || "$140k"}.
Key Skills & Competencies: ${skillsStr}.
Summary: Proven track record driving product discovery, cross-functional stakeholder alignment, and data-informed roadmap delivery.`;
      setResumeText(formatted);
      setCvFileName("Career_Profile.txt");
      toast.success("Loaded Career Profile details!");
    } else {
      toast("No Career Profile saved yet — upload your CV file");
    }
  };

  // Upload and parse CV file via Backend API
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    // Fast-path for plain text / markdown files
    if (file.name.endsWith(".txt") || file.name.endsWith(".md")) {
      try {
        const text = await file.text();
        if (text.trim()) {
          setResumeText(text);
          setCvFileName(file.name);
          toast.success(`Loaded ${file.name}`);
          setIsUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = "";
          return;
        }
      } catch (_) {}
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await api.post("/ai/parse-resume", formData);
      if (data.text) {
        setResumeText(data.text);
        setCvFileName(file.name);
        toast.success(`Extracted text from ${file.name}`);
      }
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || "Could not parse file. You can paste your CV text directly.";
      toast.error(msg);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Step 1: Run gap analysis and generate targeted questions
  const analyze = async () => {
    if (!resumeText.trim()) {
      toast.error("Please upload or enter your CV text first.");
      return;
    }
    if (!jd.trim()) {
      toast.error("Please enter the Job Description.");
      return;
    }

    setStage("analyzing");
    try {
      const { data } = await api.post("/ai/jd-customize", {
        resume_text: resumeText,
        job_description: jd,
      });
      setAnalysis(data);
      setStage("questions");
      toast.success("Gap analysis complete! Review your targeted questions.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Analysis failed. Please try again.");
      setStage("input");
    }
  };

  // Step 2: Generate rewritten customized CV from authentic answers
  const generate = async () => {
    const answeredList = Object.values(answers).filter((v) => v && v.trim().length > 0);
    if (answeredList.length === 0) {
      toast.error("Answer at least one question so your customized CV has authentic proof.");
      return;
    }

    setStage("rewriting");
    try {
      const { data } = await api.post("/ai/jd-rewrite", {
        resume_text: resumeText,
        job_description: jd,
        answers: answers,
      });
      setRewriteResult(data);
      setStage("done");
      toast.success("Customized CV & enhanced bullet points generated!");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not generate rewrite.");
      setStage("questions");
    }
  };

  const copyCustomized = () => {
    if (!rewriteResult) return;
    const textToCopy = `TAILORED PROFESSIONAL SUMMARY:
${rewriteResult.tailored_summary}

HIGH-IMPACT BULLET POINTS (MAPPED TO JD):
${(rewriteResult.enhanced_bullets || []).map((b) => `• ${b}`).join("\n")}

ORIGINAL VS CUSTOMIZED HIGHLIGHT:
Original: ${rewriteResult.original_snippet}
Enhanced: ${rewriteResult.customized_snippet}`;

    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    toast.success("Copied tailored CV content to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const questions = analysis?.questions || [];

  return (
    <div>
      <PageHead
        label="CV Customization Studio"
        title="Don't just match the JD. Answer it."
        sub="CVOLVE finds real qualification gaps, asks targeted questions, and rewrites your CV — 100% authentic, never fabricated."
      />

      <div className="grid gap-6 lg:grid-cols-2 items-start">
        {/* Left Column: CV Upload & Job Description Inputs */}
        <div className="space-y-5">
          {/* CV Upload / Input Card */}
          <div className="card-surface p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <label className="label-caps flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5 text-electric" /> Your CV / Resume
              </label>
              <button
                type="button"
                onClick={loadProfile}
                className="text-xs font-semibold text-electric hover:underline"
              >
                Use Career Profile
              </button>
            </div>

            {/* Drag / Drop / Upload Area */}
            <div className="mb-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".pdf,.docx,.txt"
                className="hidden"
                id="cv-upload-input"
              />
              <label
                htmlFor="cv-upload-input"
                className="flex cursor-pointer items-center justify-between rounded-xl border border-dashed border-border bg-secondary/40 px-4 py-3 text-xs font-medium transition-colors hover:border-electric hover:bg-secondary"
              >
                <div className="flex items-center gap-2.5">
                  {isUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-electric" />
                  ) : (
                    <UploadCloud className="h-4 w-4 text-electric" />
                  )}
                  <span>
                    {isUploading
                      ? "Extracting CV text with AI…"
                      : cvFileName
                      ? `Selected: ${cvFileName}`
                      : "Upload CV (.pdf, .docx, .txt)"}
                  </span>
                </div>
                <span className="rounded-md bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground border border-border shadow-xs">
                  Browse
                </span>
              </label>
            </div>

            {/* Editable Resume Textbox */}
            <textarea
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              rows={6}
              data-testid="cv-text-input"
              placeholder="Paste or edit your CV text here…"
              className="w-full resize-none rounded-xl border border-border bg-background p-3 text-xs leading-relaxed outline-none focus:border-electric font-mono"
            />
          </div>

          {/* Job Description Card */}
          <div className="card-surface p-5 shadow-sm">
            <label className="label-caps block mb-2">Target Job description</label>
            <textarea
              value={jd}
              onChange={(e) => setJd(e.target.value)}
              rows={6}
              data-testid="jd-input"
              placeholder="Paste the target job description or requirements here…"
              className="w-full resize-none rounded-xl border border-border bg-background p-3 text-sm leading-relaxed outline-none focus:border-electric"
            />

            <button
              onClick={analyze}
              disabled={stage === "analyzing" || isUploading}
              data-testid="jd-analyze"
              className="cv-btn-primary mt-4 w-full py-3"
            >
              {stage === "analyzing" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Analyzing qualification gaps…
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4" /> Analyze against my CV
                </>
              )}
            </button>

            {analysis?.mismatches && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5 border-t border-border pt-4">
                <p className="label-caps mb-2.5 text-coral flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5" /> Gaps & Missing Proof Identified
                </p>
                <div className="space-y-2">
                  {analysis.mismatches.map((m, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.08 }}
                      className="rounded-lg bg-coral/10 border border-coral/20 px-3 py-2 text-xs font-medium text-coral"
                    >
                      ⚠ {m}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Right Column: Dynamic Stage View (Prompt -> Questions -> Rewritten CV) */}
        <div>
          <AnimatePresence mode="wait">
            {stage === "input" && (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="card-surface flex min-h-[380px] flex-col items-center justify-center rounded-3xl border border-dashed border-border p-8 text-center"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet/10 text-violet mb-3">
                  <Wand2 className="h-7 w-7" />
                </div>
                <h3 className="font-heading text-lg font-bold">Ready to Customize</h3>
                <p className="mt-1 max-w-sm text-xs text-muted-foreground leading-relaxed">
                  Upload your CV and paste the target job description to discover exact gaps and generate interview-backed rewrites.
                </p>
              </motion.div>
            )}

            {stage === "questions" && (
              <motion.div
                key="q"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="card-surface p-6 shadow-card"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="label-caps text-violet">Targeted alignment questions</p>
                    <h3 className="font-heading text-base font-bold mt-0.5">Reveal your authentic experience</h3>
                  </div>
                  <span className="rounded-full bg-violet/10 px-2.5 py-1 text-xs font-bold text-violet">
                    {questions.length} questions
                  </span>
                </div>

                <p className="text-xs text-muted-foreground mb-4">
                  Answer these quick questions based on your real past work so CVOLVE can craft high-impact, verified bullets for this role.
                </p>

                <div className="space-y-4">
                  {questions.map((q, i) => (
                    <div key={i} className="rounded-xl border border-border/80 bg-background/50 p-3.5">
                      <p className="mb-2 text-xs font-semibold text-foreground">
                        <span className="text-violet font-bold mr-1.5">{i + 1}.</span> {q}
                      </p>
                      <textarea
                        data-testid={`jd-answer-${i}`}
                        rows={2}
                        value={answers[i] || ""}
                        onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value }))}
                        placeholder="e.g. Yes, led roadmap synchronization across 3 teams and delivered a 14% conversion lift..."
                        className="w-full resize-none rounded-lg border border-border bg-background p-2.5 text-xs outline-none focus:border-violet"
                      />
                    </div>
                  ))}
                </div>

                <button
                  onClick={generate}
                  data-testid="jd-generate"
                  className="cv-btn-primary mt-5 w-full py-3"
                >
                  Generate Tailored CV Content <ArrowRight className="h-4 w-4" />
                </button>
              </motion.div>
            )}

            {stage === "rewriting" && (
              <motion.div
                key="rewriting"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="card-surface flex min-h-[380px] flex-col items-center justify-center rounded-3xl p-8 text-center"
              >
                <Loader2 className="h-10 w-10 animate-spin text-electric mb-3" />
                <h3 className="font-heading text-lg font-bold">Rewriting & Aligning CV…</h3>
                <p className="mt-1 text-xs text-muted-foreground max-w-xs">
                  Weaving your authentic experience answers directly into tailored bullet points matching the JD requirements.
                </p>
              </motion.div>
            )}

            {stage === "done" && rewriteResult && (
              <motion.div
                key="done"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Match Score Upgrade Badge */}
                <div className="card-surface flex items-center justify-between p-4 bg-lime/10 border border-lime/20">
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-lime-600 dark:text-lime" />
                    <div>
                      <h4 className="text-xs font-bold text-foreground">JD Alignment Boost</h4>
                      <p className="text-[11px] text-muted-foreground">Authentic proof mapped directly to JD criteria</p>
                    </div>
                  </div>
                  <span className="font-heading text-xl font-black text-lime-600 dark:text-lime">
                    {analysis?.match_score || 74}% → 94%
                  </span>
                </div>

                {/* Tailored Professional Summary */}
                <div className="card-surface p-5">
                  <p className="label-caps mb-2 text-electric">Tailored Professional Summary</p>
                  <p className="text-xs text-foreground leading-relaxed bg-secondary/50 p-3 rounded-xl border border-border">
                    {rewriteResult.tailored_summary}
                  </p>
                </div>

                {/* Enhanced Bullet Points */}
                <div className="card-surface p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="label-caps text-violet">Enhanced Impact Bullets</p>
                    <button
                      onClick={copyCustomized}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-electric hover:underline"
                    >
                      {copied ? <Check className="h-3.5 w-3.5 text-lime" /> : <Copy className="h-3.5 w-3.5" />}
                      {copied ? "Copied" : "Copy All"}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(rewriteResult.enhanced_bullets || []).map((bullet, idx) => (
                      <div key={idx} className="rounded-xl border border-border/80 bg-background p-3 text-xs leading-relaxed">
                        <span className="font-bold text-violet mr-1.5">•</span>
                        {bullet}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Before vs After Comparison */}
                <div className="card-surface p-5">
                  <p className="label-caps mb-3">Original vs Customized Comparison</p>
                  <div className="space-y-2 text-xs">
                    <div className="rounded-lg bg-secondary/70 p-3 text-muted-foreground line-through">
                      <span className="font-bold mr-1">Original:</span>
                      {rewriteResult.original_snippet}
                    </div>
                    <div className="rounded-lg bg-electric/10 border border-electric/20 p-3 font-medium text-foreground">
                      <span className="font-bold text-electric mr-1">Rewritten:</span>
                      {rewriteResult.customized_snippet}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={copyCustomized}
                    className="cv-btn-primary flex-1 py-3 text-xs font-semibold"
                  >
                    <Copy className="h-4 w-4" /> Copy Tailored Content
                  </button>
                  <button
                    onClick={() => setStage("questions")}
                    className="cv-btn-ghost border border-border px-4 py-3 text-xs"
                    title="Edit Answers"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
